import type { MonetaryAmount } from '@backmarket/http-api'

export type KybMetadataResponse = {
  secondhandDealerNumberIssuingPrefectures: {
    label: string
    value: string
  }[]
  registrationTypes: {
    label: string
    value: string
  }[]
}

export type KybFile = {
  id: string
  filename: string
}

export enum StakeholderRole {
  ACCOUNT_REQUESTER = 'ACCOUNT_REQUESTER',
  LEGAL_REPRESENTATIVE = 'LEGAL_REPRESENTATIVE',
  PRIMARY_BENEFICIAL_OWNER = 'PRIMARY_BENEFICIAL_OWNER',
  SECONDARY_BENEFICIAL_OWNER = 'SECONDARY_BENEFICIAL_OWNER',
}

export type Address = {
  addressLine1: string
  addressLine2: string | null
  postalCode: string
  city: string
  stateOrProvince: string | null
  country: string
}

export type Stakeholder = {
  id: string | null
  firstName: string
  kanaFirstName?: string
  latinFirstName?: string
  lastName: string
  kanaLastName?: string
  latinLastName?: string
  dateOfBirth: string
  countryOfBirth: string
  countryOfCitizenship: string
  countryOfResidency: string
  phoneNumber: string
  address: Address
  identityDocument: {
    identificationType?: string
    countryOfIssue: string
    front: KybFile
    back: KybFile | null
  }
  roles: StakeholderRole[]
}

export enum KybStepCode {
  BUSINESS_DETAILS = 'BUSINESS_DETAILS',
  ACCOUNT_REQUESTER_DETAILS = 'ACCOUNT_REQUESTER_DETAILS',
  LEGAL_REPRESENTATIVE_DETAILS = 'LEGAL_REPRESENTATIVE_DETAILS',
  PRIMARY_BENEFICIAL_OWNER_DETAILS = 'PRIMARY_BENEFICIAL_OWNER_DETAILS',
  LETTER_OF_AUTHORIZATION = 'LETTER_OF_AUTHORIZATION',
  REVIEW = 'REVIEW',
}

export type KybStepStatus = 'TODO' | 'DONE' | 'ERROR'

export type KybStep = {
  value: KybStepCode
  label: string
  status: KybStepStatus
}

export type BusinessDetails = {
  legalName: string
  tradeName: string
  kanaLegalName?: string
  latinLegalName?: string
  registrationCountry: string
  registrationType: string
  registrationNumber: string
  incorporationDate: string
  headquartersAddress: Address
  branchesAddresses: Address[]
  registeredBusinessAddress: Address
  email: string
  investedCapital?: MonetaryAmount
  articleOfIncorporation?: KybFile
  secondhandDealerNumber?: string
  secondhandDealerNumberIssuingPrefecture?: string
  proofOfSecondHandDealerLicence?: KybFile
  w9?: KybFile
}

export type KybResponse = {
  steps: KybStep[]
  data: {
    business: BusinessDetails
    stakeholders: Stakeholder[]
    letterOfAuthorization: KybFile | null
  }
  errors: {
    entityId: string | null
    field: string
    message: string
  }[]
}

export type KybData = KybResponse['data']

export type LegalRepresentativeBody = {
  legalRepresentative: Stakeholder & {
    sameAsAccountRequester: boolean
  }
}

export type PrimaryBeneficialOwnerBody = {
  primary: Stakeholder & {
    sameAsAccountRequester: boolean
    sameAsLegalRepresentative: boolean
  }
  secondary: Stakeholder[]
}
